import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ResourceType } from '@app/core/model/resource-type';
import { IWorkgroup } from '@app/core/model/workgroup';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { NgxSmartModalService } from '@bssh/comp-lib';
import _ from 'lodash';
import { merge } from 'rxjs';
import { delay, finalize, map, switchMap, tap } from 'rxjs/operators';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { Router } from '@angular/router';
import { StringUtilities } from '@app/core/utilities/string-utilities';

interface OAuthAuthorizePostRequest {
  UrlReferrer: string;
  UserAction: 'Cancel' | 'Grant';
  AppName?: string;
  CompanyName?: string;
  LogoUrl?: string;
  ResponseType?: string;
  ClientId?: string;
  RedirectUri?: string;
  Scope?: string;
  State?: string;
  WorkgroupId?: string;
  IsPreAuth?: boolean;
  IFrame?: boolean;
}

interface ScopeItem {
  ResourceType: ResourceType,
  ResourceName: string;
  Permission: string;
}

interface OAuthAuthorizeResponse {
    ResponseType: string;
    ClientId: string;
    RedirectUri: string;
    Scope: string;
    State: string;
    Error: string;
    Code: string;
    ActingUserId: string;
    Workgroup?: IWorkgroup;
    IsPreAuth: boolean;
    UrlReferrer: string;
    ScopeItems: ScopeItem[];
    AppName: string;
    CompanyName: string;
    LogoUrl: string;
    AgreementIdEula?: number;
    AgreementIdPrivacy?: number;
    ErrorCode: string;
    IFrame: boolean;
    Workgroups: IWorkgroup[];
    ShowWorkgroupSelection: boolean;
}

@Component({
  selector: 'app-oauth-modal',
  templateUrl: './oauth-modal.component.html',
  styleUrls: ['./oauth-modal.component.scss']
})
export class OauthModalComponent extends BaseModalComponent implements AfterViewInit, OnInit {
  public modalType = 'oAuthModal';
  public modalTitle = '';
  public showCloseButton = true;
  public showConfirmButton = true;

  public workgroupControl: FormControl = new FormControl('personal');

  public alertMessages: string[];
  public errorMsg: string;

  public response: OAuthAuthorizeResponse;

  get confirmButtonText(): string {
    return this.errorMsg ? 'Back to Basespace' : 'Accept';
  }

  get oAuthCloseButtonText(): string {
    return this.errorMsg ? 'Back to the App' : `Don't Accept`;
  }
  
  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    super(ngxSmartModalService);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    const pascaledQueryParams = window.location.search.slice(1).split('&').map((queryParam) => {
      let [ key, value ] = queryParam.split('=');

      return [StringUtilities.snakeToPascal(key), value].join('=');
    }).join('&');
  
    this.subs.sink = this.http.get(`${BsApiEndPoints.OAUTH_AUTHORIZE}?${pascaledQueryParams}`).pipe(
      tap((response: OAuthAuthorizeResponse) => {
        this.response = response;
      }),
      // Need to add this to get modal to resize
      delay(0),
      finalize(() => {
        this.ngxSmartModalService.setModalData({ }, this.modalType);
        this.ngxSmartModalService.open(this.modalType);
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      })
    )
    .subscribe({
      error: (response) => {
        this.errorMsg = response.error.ErrorMessage;
        this.response = response.error.AdditionalData
      }
    });



    merge(
      this.ngxSmartModalService.getModal(this.modalType).onConfirm.pipe(map(() => 'Grant')),
      this.ngxSmartModalService.getModal(this.modalType).onClose.pipe(map(() => 'Cancel'))
    ).pipe(
      tap((userAction) => {
        // We'll just treat redirecting in error case as a side effect for simplicity
        if(this.errorMsg) {
          if(userAction === 'Grant') {
            // Back to Basespace
            this.router.navigateByUrl('/');
          } else {
            // Back to the App
            window.location.href = this.response.RedirectUri;
          }
        }
      }),
      switchMap((userAction: 'Grant' | 'Cancel') => {
        const url = `${BsApiEndPoints.OAUTH_AUTHORIZE}`;
        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });
        return this.http.post(url, this.getOAuthPostRequest(userAction), { headers });
      })
     ).subscribe(
      {
        next: (response) => {
          // This covers 2 scenarios 
          // 1. Navigating to a new app/URL after user has accepted oauth
          // 2. Navigating back to referrer page if user cancels/disallows oauth
          window.location.href = response['Url'];
        },
        error: (response) => {
          this.errorMsg = response.error.ErrorMessage;
        }
      });
  }

  getOAuthPostRequest(userAction: 'Grant' | 'Cancel'): OAuthAuthorizePostRequest {
    return {
      UrlReferrer: this.response.UrlReferrer,
      UserAction: userAction,
      ResponseType: this.response.ResponseType,
      ClientId: this.response.ClientId,
      RedirectUri: this.response.RedirectUri,
      Scope: this.response.Scope,
      State: this.response.State,
      WorkgroupId: this.workgroupControl.value,
      IsPreAuth: this.response.IsPreAuth,
      IFrame: this.response.IFrame
    }
  }

  getAgreementUrl(agreementId: number | string): string {
    return `/agreements/view?agreementid=${agreementId}`;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

}
